/* eslint-disable max-lines */
import { FC, ReactNode, useMemo } from "react";
import { IEsimLikeForLikeReport, IEsimLikeForLikeRow } from "../../../interfaces/esim/EsimMargin.types";
import { faceValueNote, gapLabel, gapTextClass, gb, money, packageLabel } from "../../../utils/esim/margin";
import { bySlot } from "../../../utils/esim/priceCurve";
import { WithTip } from "../../ui/InfoTip";
import NotfoundTable from "../../ui/NotfoundTable";
import Table, { Row } from "../../ui/table";

interface Props {
  report: IEsimLikeForLikeReport | null;
  loading: boolean;
}

const BASKET_TIP =
  "Për çdo paketë tonën, plani më i lirë i çdo konkurrenti që jep të paktën aq GB për të paktën aq ditë " +
  "në të njëjtin destinacion: paketë për paketë, jo çmim për GB. Paketat rajonale krahasohen te rajoni " +
  "i tyre (Europë, Ballkan, Global). Çmimet jo në euro tregohen në monedhën e tyre, pa konvertim.";

const SHORT_VALIDITY_TIP =
  "*: asnjë plan i konkurrentit nuk zgjat sa paketa jonë; tregohet më i liri që jep sasinë.";

const GAP_TIP = "Sa më shtrenjtë jemi se plani më i lirë që e mbulon; negative kur jemi më lirë.";

// "3 GB / 30 ditë", "Pa limit / 7 ditë": what the customer buys, which is what the basket is.
const basketLabel = (r: IEsimLikeForLikeRow) =>
  `${r.isUnlimited ? "Pa limit" : gb(r.allowanceGb)} / ${r.validityDays} ditë`;

const LikeForLikeTable: FC<Props> = ({ report, loading }) => {
  // A competitor keeps its column whichever destination is open, in the price curve's order.
  const competitors = useMemo(() => {
    const names = new Set<string>();
    (report?.rows ?? []).forEach((r) => r.byCompetitor.forEach((m) => names.add(m.competitor)));
    return Array.from(names).sort(bySlot);
  }, [report]);

  const rows = useMemo(
    () =>
      [...(report?.rows ?? [])].sort(
        (a, b) =>
          Number(a.isUnlimited) - Number(b.isUnlimited) ||
          a.allowanceGb - b.allowanceGb ||
          a.validityDays - b.validityDays
      ),
    [report]
  );

  const shortValidity = rows.some((r) => r.byCompetitor.some((m) => !m.validityCovered));
  const titles = [
    <WithTip
      key="basket"
      label="Paketa"
      tip={shortValidity ? `${BASKET_TIP} ${SHORT_VALIDITY_TIP}` : BASKET_TIP}
    />,
    "Gjirafa",
    ...competitors,
    "Më i liri",
    "Çmimi i tij",
    <WithTip key="gap" label="Diferenca" tip={GAP_TIP} />
  ];

  return (
    <>
      <Table titles={titles} loading={loading} style="hover">
        {rows.map((r) => {
          const cells: Record<string, ReactNode> = {};
          competitors.forEach((name, i) => {
            const m = r.byCompetitor.find((x) => x.competitor === name);
            cells[`c${i}`] = m ? (
              <span
                className={m.price < r.ourPrice ? "text-red" : ""}
                title={
                  `${m.planName}: ${m.isUnlimited ? "pa limit" : gb(m.dataGb)}, ` +
                  `${m.validityDays ?? "?"} ditë` +
                  faceValueNote(m.currency) +
                  (m.validityCovered
                    ? ""
                    : ". Asnjë plan i tyre nuk zgjat sa i yni; ky jep sasinë, por më pak ditë")
                }
              >
                {money(m.price, m.currency)}
                {!m.validityCovered && <span className="text-neutral"> *</span>}
                <span className="block text-neutral text-xs whitespace-nowrap">
                  {m.isUnlimited ? "pa limit" : gb(m.dataGb)} / {m.validityDays ?? "?"} ditë
                </span>
              </span>
            ) : (
              <span className="text-neutral" title="Asnjë plan që mbulon sasinë">–</span>
            );
          });
          return (
            <Row
              key={`${r.packageTemplateId}-${r.countryIso2}`}
              basket={
                <span title={packageLabel(r.packageName) ?? ""}>
                  <span className="font-medium">{basketLabel(r)}</span>
                  <span className="block text-neutral text-xs truncate max-w-[220px]">
                    {packageLabel(r.packageName)}
                  </span>
                </span>
              }
              ours={<span className="font-medium">{money(r.ourPrice, "EUR")}</span>}
              {...cells}
              cheapest={r.cheapest?.competitor ?? "–"}
              theirPrice={
                r.cheapest ? (
                  <span>
                    {money(r.cheapest.price, r.cheapest.currency)}
                    <span className="block text-neutral text-xs whitespace-nowrap">
                      {r.cheapest.isUnlimited ? "pa limit" : gb(r.cheapest.dataGb)} /{" "}
                      {r.cheapest.validityDays ?? "?"} ditë
                    </span>
                  </span>
                ) : (
                  "–"
                )
              }
              gap={
                <span
                  className={gapTextClass(r.cheapest?.gapPercent ?? null)}
                  title={r.cheapest ? faceValueNote(r.cheapest.currency).trim() || undefined : undefined}
                >
                  {gapLabel(r.cheapest?.gapPercent ?? null, r.cheapest?.currency)}
                </span>
              }
            />
          );
        })}
      </Table>
      {!loading && rows.length === 0 && (
        <NotfoundTable
          header="Nuk ka paketa"
          paragraph={
            "Asnjë paketë e jona me çmim nuk shitet në këtë destinacion, " +
            "ose asnjë konkurrent nuk u lexua aty."
          }
        />
      )}
    </>
  );
};

export default LikeForLikeTable;
